'use client';

import React from 'react';
import { Text } from '@react-three/drei';

// ElectricityMeter component adapted for SLD usage
// Props: position, size, onHover, component, type, onClick
export default function ElectricityMeter({
  position = [0, 0, 0],
  size = [1, 1, 0.35],
  onHover,
  component,
  type = 'grid',
  onClick,
}) {
  const [width = 1, height = 1, depth = 0.35] = size || [];

  // Baseline dimensions used in the original model
  const base = { w: 1.0, h: 1.0, d: 0.35 };
  const scale = [width / base.w, height / base.h, depth / base.d];

  // Helper to safely call handlers
  const safeHover = (val) => onHover && onHover(val);
  const safeClick = () => onClick && onClick(component);

  return (
    <group position={position} scale={scale}>
      {/* Main meter body - interactive */}
      <mesh
        position={[0, 2, 0]}
        castShadow
        receiveShadow
        onPointerOver={() => safeHover(component?.name)}
        onPointerOut={() => safeHover(null)}
        onClick={() => safeClick()}
      >
        <boxGeometry args={[1.0, 1.0, 0.35]} />
        <meshStandardMaterial color='#FFFFFF' metalness={0.4} roughness={0.3} />
      </mesh>

      {/* Display panel */}
      <mesh position={[0, 2.1, 0.2]}>
        <boxGeometry args={[0.75, 0.55, 0.02]} />
        <meshStandardMaterial color='#FFFFFF' />
      </mesh>

      {/* Digital counter area */}
      <mesh position={[0, 2.2, 0.22]}>
        <boxGeometry args={[0.65, 0.25, 0.01]} />
        <meshStandardMaterial color='#F5F5F5' />
      </mesh>

      {/* Counter digits */}
      <mesh position={[-0.2, 2.22, 0.235]}>
        <boxGeometry args={[0.25, 0.15, 0.01]} />
        <meshStandardMaterial color='#000000' />
      </mesh>
      <mesh position={[0.15, 2.22, 0.235]}>
        <boxGeometry args={[0.08, 0.15, 0.01]} />
        <meshStandardMaterial color='#FF0000' />
      </mesh>

      {/* Arrow indicator */}
      <group position={[0, 1.95, 0.22]}>
        <mesh>
          <boxGeometry args={[0.5, 0.06, 0.01]} />
          <meshStandardMaterial color='#000000' />
        </mesh>
        <mesh position={[0.28, 0, 0]}>
          <coneGeometry args={[0.08, 0.12, 4]} rotation={[0, 0, Math.PI / 2]} />
          <meshStandardMaterial color='#000000' />
        </mesh>
      </group>

      {/* Barcode */}
      <mesh position={[0, 1.75, 0.22]}>
        <boxGeometry args={[0.45, 0.08, 0.01]} />
        <meshStandardMaterial color='#FFFFFF' />
      </mesh>
      {[...Array(8)].map((_, i) => (
        <mesh key={i} position={[-0.18 + i * 0.055, 1.75, 0.225]}>
          <boxGeometry args={[0.02, 0.07, 0.01]} />
          <meshStandardMaterial color='#000000' />
        </mesh>
      ))}

      {/* Connector cap - clickable area as well */}
      <mesh
        position={[0, 1.35, 0]}
        castShadow
        onPointerOver={() => safeHover(component?.name)}
        onPointerOut={() => safeHover(null)}
        onClick={() => safeClick()}
      >
        <boxGeometry args={[0.9, 0.35, 0.3]} />
        <meshStandardMaterial color='#000000' />
      </mesh>

      {/* Lightning bolt decoration */}
      <mesh position={[0, 1.42, 0.12]}>
        <boxGeometry args={[0.08, 0.18, 0.02]} />
        <meshStandardMaterial color='#FFFFFF' />
      </mesh>
      <mesh position={[0.08, 1.48, 0.12]} rotation={[0, 0, -0.6]}>
        <boxGeometry args={[0.08, 0.12, 0.02]} />
        <meshStandardMaterial color='#FFFFFF' />
      </mesh>
      <mesh position={[-0.08, 1.32, 0.12]} rotation={[0, 0, 0.6]}>
        <boxGeometry args={[0.08, 0.12, 0.02]} />
        <meshStandardMaterial color='#FFFFFF' />
      </mesh>

      {/* Terminal pins - clickable; highlight via onHover from parent if desired */}
      <group position={[0, 0.95, 0]}>
        {[-0.25, 0, 0.25].map((x, i) => (
          <mesh
            key={i}
            position={[x, 0, 0]}
            castShadow
            onPointerOver={() => safeHover(component?.name)}
            onPointerOut={() => safeHover(null)}
            onClick={() => safeClick()}
          >
            <boxGeometry args={[0.08, 0.3, 0.08]} />
            <meshStandardMaterial
              color='#C0C0C0'
              metalness={0.9}
              roughness={0.1}
            />
          </mesh>
        ))}
      </group>

      {/* Name label */}
      <group position={[-1, height, 0]}>
        {/* First line - BIG FONT */}
        <Text
          fontSize={0.34}
          color='black'
          anchorX='left'
          anchorY='bottom'
          outlineColor='#000000ff'
          fontWeight={700}
        >
          {component.name}
        </Text>

        {/* Next 2 lines - SMALL FONT only if value exists */}
        {(component.details?.Brand_name ||
          component.details?.Quantity ||
          component.details?.Specification) && (
          <Text fontSize={0.9} color='black' anchorX='left' anchorY='top'>
            {`${component.details?.Brand_name || ''}\nx${component.details?.Quantity || '0'}\n${component.details?.Specification || ''}`}
          </Text>
        )}
      </group>
    </group>
  );
}
